import { action, computed, runInAction } from 'mobx'
import ListStore from '../../../shared-lib/stores/ListStore'
import ConsultationFormModel, { $ConsultationFormModel } from './ConsultationFormModel'
import { scopeData } from '../../lib/scopeData'

// The shape the API accepts back: ConsultationForm carries fields (professionalId, questionIds)
// that ConsultationFormInput doesn't define, so a fetched form can't be sent straight back up.
export type $ConsultationFormInput = {
  _id: string
  name: string
  questions: ConsultationFormQuestionInput[]
  treatmentIds: string[]
  applyToAllTreatments: boolean
}

export default class ConsultationFormStore extends ListStore<$ConsultationFormModel> {
  fetchMethodName = 'consultationForms'
  ModelClass = ConsultationFormModel

  // Narrows a form from the API (or an edited copy of one) to the fields the mutation accepts
  toFormInput = (form: $ConsultationFormInput | ConsultationForm): $ConsultationFormInput => {
    const { _id, name, questions, treatmentIds, applyToAllTreatments } = form
    return { _id, name, questions, treatmentIds, applyToAllTreatments }
  }

  @action.bound async consultationForm_api(_id: string, professionalId: string) {
    const { communication: { requester } } = this.rootStore

    this.loading = true
    try {
      const { consultationForm } = await requester.consultationForm({ _id, professionalId })
      return consultationForm
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async createConsultationForm_api(form: $ConsultationFormInput) {
    const { communication: { requester } } = this.rootStore

    this.loading = true
    try {
      const { createOrUpdateConsultationForm } = await requester.createOrUpdateConsultationForm({ data: this.toFormInput(form) })

      if (createOrUpdateConsultationForm) {
        this.mergeData([createOrUpdateConsultationForm])
      }

      return createOrUpdateConsultationForm
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async consultationFormCompleted(externalBookingId: string) {
    const { communication: { requester } } = this.rootStore

    this.loading = true
    try {
      const { consultationFormCompleted } = await requester.consultationFormCompleted({ externalBookingId })
      return consultationFormCompleted
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async consultationFormAnswers(externalBookingId: string) {
    const { communication: { requester } } = this.rootStore

    this.loading = true
    try {
      const { consultationFormAnswers } = await requester.consultationFormAnswers({ externalBookingId })
      return consultationFormAnswers
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async fetchFormsForPro_api() {
    const { userStore } = this.rootStore
    return this.fetchItems_api({ variables: { professionalId: userStore.data._id }, clear: true })
  }

  @computed get scopedItems() {
    const dataScopeId = this.rootStore.authStore.dataScopeId
    if (!dataScopeId) return this.items
    return scopeData(this.items, dataScopeId, 'ownerId')
  }

  @computed get ownItems() {
    const ownId = this.rootStore.userStore.data._id
    return scopeData(this.items, ownId, 'ownerId')
  }
}

export type $ConsultationFormStore = ConsultationFormStore
